import ManagerialAuditForm from '@/components/Reports/ManagerialReports/ManagerialAuditForm';
import { DefaultRouteTabs, Group } from '@/components/_common';
import { WorkOrderCalendarDateType } from '@/core/domain/workorder/WorkOrders/WorkOrders.enum';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { PageProps } from '@/types/PageProps';
import { Button, Card, Divider, Empty, Form } from 'antd';
import moment from 'moment';
import { useState } from 'react';
import { Repeat } from 'react-feather';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import AuditComplementsTable from './AuditComplementsTable';
import { WorkOrderAuditSituationType } from '@/core/domain/workorder/AuditData/AuditData.enums';
import { WorkOrderAuditQueryParams } from '@/core/domain/workorder/AuditData/AuditData.types';
import {
  AuditDataComplementItemsSituationTypeMapping,
  AuditDataComplementsSituationTypeMapping,
  AuditDataSalesSituationTypeMapping,
} from '@/core/domain/workorder/AuditData/AuditData.constants';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import {
  QUERY_WORKORDER_AUDIT_COMPLEMENT_ITEMS,
  QUERY_WORKORDER_AUDIT_COMPLEMENTS,
  QUERY_WORKORDER_AUDIT_SALES,
} from '@/core/domain/workorder/AuditData/AuditData.hooks';
import AuditSalesTable from './AuditSalesTable';
import AuditComplementItemsTable from './AuditComplementItemsTable';

export interface ManagerialAuditFormValues {
  rangeDate: [moment.Moment, moment.Moment];
  companiesIds: number[];
  situationType: WorkOrderAuditSituationType;
  workOrderStatus: WorkOrderComplementStatus;
  workOrderDateType: WorkOrderCalendarDateType;
}

export default function ManagerialAuditPage({ route }: PageProps) {
  const intl = useIntl().formatMessage;
  const [form] = Form.useForm<ManagerialAuditFormValues>();

  const [loading, setLoading] = useState(false);
  const [queryParams, setQueryParams] = useState<WorkOrderAuditQueryParams>();

  const queryChanel = useQueryChanel();

  const handleValidate = async () => {
    setLoading(true);
    try {
      const { rangeDate, companiesIds, ...values } =
        await form.validateFields();

      const [startDate, endDate] = rangeDate;

      const params: WorkOrderAuditQueryParams = {
        ...values,
        companiesIds: companiesIds?.toString(),
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
      };

      queryChanel({
        removeQueries: [
          [QUERY_WORKORDER_AUDIT_COMPLEMENTS],
          [QUERY_WORKORDER_AUDIT_SALES],
          [QUERY_WORKORDER_AUDIT_COMPLEMENT_ITEMS],
        ],
      });

      setQueryParams((prev) => ({ ...prev, ...params }));

      setTimeout(() => {
        setLoading(false);
      }, 4000);
    } catch {
      setLoading(false);
    }
  };

  const mappedComplementSituation =
    queryParams?.situationType &&
    queryParams?.situationType in AuditDataComplementsSituationTypeMapping
      ? AuditDataComplementsSituationTypeMapping[queryParams.situationType]
      : undefined;

  const mappedSalesSituation =
    queryParams?.situationType &&
    queryParams?.situationType in AuditDataSalesSituationTypeMapping
      ? AuditDataSalesSituationTypeMapping[queryParams.situationType]
      : undefined;

  const mappedComplementItemsSituation =
    queryParams?.situationType &&
    queryParams?.situationType in AuditDataComplementItemsSituationTypeMapping
      ? AuditDataComplementItemsSituationTypeMapping[queryParams.situationType]
      : undefined;

  return (
    <DefaultRouteTabs
      route={route.parent}
      permissions={route.permissions}
      extra={
        <Button
          block
          type="primary"
          onClick={handleValidate}
          disabled={loading}
        >
          <Group position="center">
            <Repeat size={14} />
            Validar
          </Group>
        </Button>
      }
    >
      <Helmet title={intl({ id: route.name })} />

      <ManagerialAuditForm form={form} />

      {mappedComplementSituation && (
        <AuditComplementsTable
          queryParams={{
            companiesIds: queryParams?.companiesIds,
            startDate: queryParams?.startDate,
            endDate: queryParams?.endDate,
            situationType: mappedComplementSituation,
            workOrderItemTypes: queryParams?.workOrderItemTypes?.toString(),
          }}
        />
      )}

      {mappedComplementItemsSituation && (
        <>
          <AuditComplementItemsTable
            queryParams={{
              ...queryParams,
              situationType: mappedComplementItemsSituation,
            }}
          />

          <Divider />
        </>
      )}

      {mappedSalesSituation && (
        <AuditSalesTable
          queryParams={{
            companiesIds: queryParams?.companiesIds,
            startDate: queryParams?.startDate,
            endDate: queryParams?.endDate,
            situationType: mappedSalesSituation,
          }}
        />
      )}

      {!mappedComplementSituation &&
        !mappedSalesSituation &&
        !mappedSalesSituation && (
          <Card type="inner" style={{ borderRadius: 10 }}>
            <Empty />
          </Card>
        )}
    </DefaultRouteTabs>
  );
}
